import { useEffect, useRef, useState } from "react";
import { Annotation, Compartment, EditorState } from "@codemirror/state";
import { EditorView, keymap, placeholder } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, isolateHistory } from "@codemirror/commands";
import { Play, CornerDownLeft, X } from "lucide-react";
import { ghostSuggestion, setGhostSuggestion } from "./ghost-text.js";
import { storyStructure } from "./story-structure.js";
import "./WritingView.css";

export interface WritingSelection { readonly from: number; readonly to: number; readonly text: string }
export interface WritingViewProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly suggestion?: string;
  readonly onAcceptSuggestion?: (acceptedText: string) => void;
  readonly onDismissSuggestion?: () => void;
  readonly onRequestSuggestion?: (selection: WritingSelection) => void;
  readonly onSelectionChange?: (selection: WritingSelection) => void;
  readonly onPreviewHere?: () => void;
  readonly suggesting?: boolean;
  readonly aiEnabled?: boolean;
  readonly onCompositionChange?: (composing: boolean) => void;
  readonly disabled?: boolean;
  readonly status?: string;
  readonly title?: string;
}

const externalChange = Annotation.define<boolean>();
const getSelection = (view: EditorView): WritingSelection => {
  const { from, to } = view.state.selection.main;
  return { from, to, text: view.state.sliceDoc(from, to) };
};

/** One continuous CodeMirror document: native selection, IME and undo, no script syntax. */
export function WritingView(props: WritingViewProps) {
  const mount = useRef<HTMLDivElement>(null);
  const editor = useRef<EditorView | null>(null);
  const latest = useRef(props);
  latest.current = props;
  const editable = useRef(new Compartment());
  const [visibleSuggestion, setVisibleSuggestion] = useState("");

  const dismiss = () => {
    const view = editor.current;
    if (view?.state.field(ghostSuggestion)) view.dispatch({ effects: setGhostSuggestion.of(null) });
  };
  const accept = (): boolean => {
    const view = editor.current;
    const ghost = view?.state.field(ghostSuggestion);
    if (!view || !ghost || view.composing || latest.current.disabled || latest.current.aiEnabled === false) return false;
    view.dispatch({
      changes: { from: ghost.position, insert: ghost.text },
      selection: { anchor: ghost.position + ghost.text.length },
      annotations: [isolateHistory.of("full")],
      userEvent: "input.complete",
    });
    latest.current.onAcceptSuggestion?.(ghost.text);
    view.focus();
    return true;
  };
  const request = () => {
    const view = editor.current;
    if (view && !view.composing && !latest.current.disabled && latest.current.aiEnabled !== false) {
      const selection = getSelection(view);
      if (selection.from !== selection.to) view.dispatch({ selection: { anchor: selection.to } });
      latest.current.onRequestSuggestion?.(selection);
      view.focus();
    }
  };

  useEffect(() => {
    if (!mount.current) return;
    const view = new EditorView({
      parent: mount.current,
      state: EditorState.create({
        doc: latest.current.value,
        selection: { anchor: latest.current.value.length },
        extensions: [
          history(), ghostSuggestion, storyStructure, EditorView.lineWrapping,
          editable.current.of([EditorView.editable.of(!latest.current.disabled), EditorState.readOnly.of(!!latest.current.disabled)]),
          placeholder("Start with a line of dialogue, a moment, a scene. Your story begins here…"),
          EditorView.contentAttributes.of({ "aria-label": "Story manuscript", spellcheck: "true", "aria-describedby": "writing-keyboard-hint" }),
          EditorView.domEventHandlers({
            compositionstart: () => { latest.current.onCompositionChange?.(true); },
            compositionend: () => { latest.current.onCompositionChange?.(false); },
          }),
          keymap.of([
            { key: "Tab", run: () => accept() },
            { key: "Mod-Enter", run: (v) => {
              if (v.composing || latest.current.disabled || !latest.current.onPreviewHere) return false;
              latest.current.onPreviewHere(); return true;
            } },
            { key: "Escape", run: (v) => {
              if (v.composing || !v.state.field(ghostSuggestion)) return false;
              dismiss(); return true;
            } },
            { key: "Mod-j", run: (v) => { if (v.composing) return false; request(); return true; } },
            ...defaultKeymap, ...historyKeymap,
          ]),
          EditorView.updateListener.of((update) => {
            const before = update.startState.field(ghostSuggestion);
            const after = update.state.field(ghostSuggestion);
            if (before !== after) setVisibleSuggestion(after?.text ?? "");
            if (before && !after && !update.transactions.some((t) => t.isUserEvent("input.complete"))) {
              latest.current.onDismissSuggestion?.();
            }
            if (update.docChanged && !update.transactions.some((t) => t.annotation(externalChange))) {
              latest.current.onChange(update.state.doc.toString());
            }
            if (update.selectionSet || update.docChanged) {
              const selection = getSelection(update.view);
              latest.current.onSelectionChange?.(selection);
            }
          }),
        ],
      }),
    });
    editor.current = view;
    latest.current.onSelectionChange?.(getSelection(view));
    return () => { editor.current = null; view.destroy(); };
  }, []);

  useEffect(() => {
    const view = editor.current;
    if (view && props.value !== view.state.doc.toString()) {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: props.value }, annotations: externalChange.of(true) });
    }
  }, [props.value]);
  useEffect(() => {
    editor.current?.dispatch({ effects: editable.current.reconfigure([EditorView.editable.of(!props.disabled), EditorState.readOnly.of(!!props.disabled)]) });
  }, [props.disabled]);
  useEffect(() => {
    const view = editor.current;
    if (!view) return;
    const selection = view.state.selection.main;
    view.dispatch({ effects: setGhostSuggestion.of(props.suggestion && props.aiEnabled !== false && !props.disabled && !view.composing && selection.empty
      ? { position: selection.head, text: props.suggestion } : null) });
  }, [props.suggestion, props.disabled, props.aiEnabled]);

  return <section className="writing-view" aria-label="Write your story">
    <header className="writing-header">
      <div><span className="writing-eyebrow">MANUSCRIPT</span><h1>{props.title || "A new story"}</h1></div>
      {props.onPreviewHere && <button className="writing-preview" disabled={props.disabled} onClick={props.onPreviewHere} title="Play from here · ⌘/Ctrl Enter">
        <Play size={14} aria-hidden="true" />Play from here
      </button>}
    </header>
    <div className="writing-editor" ref={mount} />
    <footer className="writing-footer">
      <span className="writing-status" role="status">{props.status || "Your story, in your words"}</span>
      {visibleSuggestion ? <span id="writing-keyboard-hint" className="writing-completion-actions">
        <button onClick={accept} disabled={props.disabled}><CornerDownLeft size={13} aria-hidden="true" /><kbd>Tab</kbd> Accept suggestion</button>
        <button onClick={dismiss} aria-label="Dismiss suggestion" title="Esc to dismiss"><X size={14} /></button>
      </span> : <span id="writing-keyboard-hint" className="writing-hint" role="status">
        <span className={`writing-ai-dot${props.suggesting ? " is-thinking" : ""}${props.aiEnabled === false ? " is-off" : ""}`} aria-hidden="true" />
        {props.aiEnabled === false ? "AI suggestions off" : props.suggesting ? "Finding the next words…" : "Suggestions appear as you write · Tab to accept"}
      </span>}
    </footer>
  </section>;
}
